// Visual tokens. Palette pulled from the BattleShark logo: deep navy
// canvas, steel-silver foreground, bright cyan as the active/HUD accent.
// Phaser is inconsistent about expecting hex strings vs 0xRRGGBB integers,
// so each color is exported in whichever form it's primarily consumed.

// --- Fonts ---
// Self-hosted via @fontsource, imported in src/main.ts. Rajdhani is the
// e-sports / tactical header face that matches the logo's "BATTLESHARK"
// wordmark; Kode Mono is the body face (terminal-readout feel); JetBrains
// Mono is reserved for "tactical data" — turn number, unit counts, dice.
// System-font fallbacks cover the gap before the woff2 files load.
export const FONT_HEADER =
  '"Rajdhani", "Segoe UI", system-ui, -apple-system, sans-serif';
// Body uses Kode Mono — a designed monospace that reads like part of an
// interactive terminal interface. Gives every action-panel string a
// "tactical readout" feel instead of plain sans body copy.
export const FONT_BODY =
  '"Kode Mono", "Consolas", "Segoe UI", system-ui, monospace';
export const FONT_MONO =
  '"JetBrains Mono", "Consolas", "Courier New", monospace';

// Back-compat alias — anything still using FONT_FAMILY gets the body font.
export const FONT_FAMILY = FONT_BODY;

// --- Palette ---
export const COLORS = {
  // Backgrounds
  bgDeepInt: 0x060d17, // canvas behind everything
  bgPanelInt: 0x0e1a2b, // HUD + action strip
  bgElevatedInt: 0x16263f, // button fills, tile cards
  bgDeepHex: '#060d17',

  // Borders
  borderSubtleInt: 0x1f324f,
  borderBrightInt: 0x2e4f7a,

  // Text
  textPrimary: '#e8eef5',
  textSecondary: '#9ba8bc',
  textSecondaryInt: 0x9ba8bc,
  textMuted: '#5c6b82',
  textOnNode: '#ffffff',

  // Nav bar
  navIconActiveInt: 0x4fc3ff,
  navIconInactiveInt: 0x6e7e92,

  // Accents (the "modern military terminal" colors)
  accentCyanInt: 0x4fc3ff,
  accentCyanHex: '#4fc3ff',
  accentAmberInt: 0xffb347,
  accentAmberHex: '#ffb347',
  accentRedInt: 0xff5470,
  accentRedHex: '#ff5470',
  accentGreenInt: 0x4ade80,
  accentGreenHex: '#4ade80',

  // Map graphics
  adjacencyInt: 0x2e4f7a,
  hexGridInt: 0x1a2b44,

  // Territory rings — selected = own (bright lime), target = enemy (red),
  // destination = friendly (cyan, defined elsewhere as accentCyanInt).
  nodeStrokeInt: 0xc9d3e1,
  selectedStrokeInt: 0xadff2f,
  attackTargetStrokeInt: 0xff5470,

  // Dice — real dice look. Settled face is white with black pips; loss
  // tints the face red but keeps the pips so the rolled value is still
  // visible. The "?" preview keeps a slightly off-white face so the cyan
  // border and cyan question-mark read as "this die WILL roll".
  diceFillInt: 0xf3f5fa,
  diceStrokeInt: 0x4fc3ff,
  diceTextHex: '#4fc3ff',
  diceLossFillInt: 0xff5470,
  diceLossTextHex: '#ffffff',
  dicePipDarkInt: 0x0e1a2b, // near-black, slightly warm so it doesn't bleed
  dicePipLightInt: 0xffffff, // unused for now; reserved for inverted styles

  // Idle (grayed) dice in the always-visible tray.
  diceIdleFillInt: 0x10182a,
  diceIdleStrokeInt: 0x2a3140,

  // Dice tray strip — semi-transparent navy that sits between the map
  // and the action panel.
  diceTrayBgInt: 0x132540,

  // Buttons
  buttonBgInt: 0x16263f,
  buttonBgHoverInt: 0x1d3252,
  buttonBgDisabledInt: 0x0e1a2b,
  buttonBgPrimaryInt: 0x103048,
  buttonBgPrimaryHoverInt: 0x174566,
  buttonStrokeInt: 0x2e4f7a,
  buttonStrokePrimaryInt: 0x4fc3ff,
} as const;

// --- Team palette ---
// Marine-combat color set. Each color is bright + saturated for
// high-contrast on the dark canvas, and chosen so it doesn't fight
// any of the 4 muted player colors (Maroon / Navy / Forest / Purple).
// Indexed by team-1 (T1 = TEAM_COLORS_INT[0], etc.).
//
//   T1 Aqua    — bioluminescent cyan
//   T2 Solar   — warm orange
//   T3 Rose    — hot magenta-pink
//   T4 Kelp    — fluorescent lime-green
export const TEAM_COLORS_INT: readonly number[] = [
  0x00e5ff, // T1 — Aqua
  0xff8c1a, // T2 — Solar
  0xff3da3, // T3 — Rose
  0xa8ff60, // T4 — Kelp
];

export const TEAM_COLORS_HEX: readonly string[] = [
  '#00e5ff',
  '#ff8c1a',
  '#ff3da3',
  '#a8ff60',
];

/** Look up the team color (int form) for a given team number. */
export function teamColorInt(team: number): number {
  const idx = (team - 1) % TEAM_COLORS_INT.length;
  return TEAM_COLORS_INT[idx] ?? TEAM_COLORS_INT[0]!;
}

/** Look up the team color (hex string form) for a given team number. */
export function teamColorHex(team: number): string {
  const idx = (team - 1) % TEAM_COLORS_HEX.length;
  return TEAM_COLORS_HEX[idx] ?? TEAM_COLORS_HEX[0]!;
}

// --- Geometry ---
// Square territory tiles. Squares within a region sit flush so they read
// as one continent; the cross-region bridges are drawn as adjacency lines.
export const TERRITORY_SIZE = 80;
export const TERRITORY_STROKE_WIDTH = 3;
// Wider so the lime-green selection ring extends visibly inside AND
// outside the tile edge — clear which territory you're acting on.
export const TERRITORY_SELECTED_STROKE_WIDTH = 10;

export const ADJACENCY_LINE_WIDTH = 2;
export const ADJACENCY_LINE_ALPHA = 0.55;

export const BUTTON_HEIGHT = 72;
export const BUTTON_WIDTH = 240;
export const BUTTON_RADIUS = 6;
export const BUTTON_STROKE_WIDTH_DEFAULT = 1;
export const BUTTON_STROKE_WIDTH_PRIMARY = 2;

export const PANEL_DIVIDER_ALPHA = 0.7;
